/**
 * DeviceHelper is used to contain auxiliary functions for Device entity
 */

import {
	ConnectedDeviceSummary,
	DeviceAreaOption,
	DeviceSummary,
	DeviceType,
	LocalDeviceFilter,
	MeasurementUnit,
	MeasurementUnits,
	UnitSymbol,
	UnitSymbolLiteral,
	WaterManagementAreaType,
} from '@types';

export const variableDialMaxLength = 13;
export const variableDialPattern = '^$|^\\d{1,12}$|^\\d{0,11}(\\.\\d{0,11})?$';

/**
 * Returns total digits of the meter's dial
 * @param {string} dials dial type with patern 'm' followed by 2 digits (e.g. 'm34')
 * @returns number of digits in total for the dial type, or 0 if dial hasn't right pattern
 */
export const calculateMeterDialDigitsNumber = (dials: string | null | undefined) => {
	if (!dials || !isAnalogMeterDial(dials)) {
		return 0;
	}
	const numberOfDigitsBefore = Number(dials.charAt(1));
	const numberOfDigitsAfter = Number(dials.charAt(2));
	const numberOfDigitsTotal = numberOfDigitsBefore + numberOfDigitsAfter;
	return numberOfDigitsTotal;
};

/**
 * Insert decimal point at the correct position based on the dials type
 * @param {string} readingValue the reading value to be formatted
 * @param {string} dialsType the dials type
 * @returns {string} the formatted reading value with decimal point
 */
export const insertDecimalPoint = (readingValue: string, dialsType: string) => {
	const numberOfDigitsBefore = Number(dialsType?.charAt(1));
	return (
		readingValue.slice(0, numberOfDigitsBefore) + '.' + readingValue.slice(numberOfDigitsBefore)
	);
};

export const compareDevices = <AnyDevice extends DeviceBase>(a: AnyDevice, b: AnyDevice) => {
	const aname = displayName(a);
	const bname = displayName(b);
	return aname.localeCompare(bname);
};

export const displayName = <AnyDevice extends DeviceBase>(device: AnyDevice) => {
	return device.displayName && device.displayName != '' ? device.displayName : device.reference;
};

/**
 * Validate whether a dial value satisfy the pattern of dial type
 * @param {string} dials dial type
 * @returns {boolean} dial type pattern's validation
 */
export const isAnalogMeterDial = (dials: string | null | undefined) => {
	if (!dials) {
		return false;
	}
	const dialTypePattern = '[m][0-9]{2}';
	return RegExp(dialTypePattern).test(dials);
};

/**
 * Validate whether a dial value satisfy the pattern of dial type
 * @param {string} dials dial type
 * @returns {boolean} dial type pattern's validation
 */
export const isDeviceWithVariableDial = (device: DeviceType) => {
	return device.dialsType?.dialsType === 'Variable';
};

export function mapDeviceOptionToWaterManagementArea(deviceAreaOption: DeviceAreaOption) {
	switch (deviceAreaOption) {
		case DeviceAreaOption.SUPPLEMENTED:
			return WaterManagementAreaType.SUPPLEMENTED;
		case DeviceAreaOption.UNSUPPLEMENTED:
			return WaterManagementAreaType.UNSUPPLEMENTED;
		case DeviceAreaOption.ALL:
			return undefined;
		default:
			return undefined;
	}
}

export function sortAscDevicesByDisplayNameAndReference<AnyDevice extends DeviceBase>(
	devices: AnyDevice[],
) {
	return devices.sort((a, b) => {
		return compareDevices(a, b);
	});
}

export const unitsSymbolsAndNames: MeasurementUnit[] = Object.keys(MeasurementUnits).map(key => {
	const { symbol, name } = MeasurementUnits[key];
	return { symbol, name };
});

export const unitSymbols: UnitSymbol[] = Object.keys(MeasurementUnits).map(
	key => MeasurementUnits[key].symbol,
);

type DeviceBase = { displayName?: string; reference: string };

export const convertToML = (value: number, unit: UnitSymbol): number => {
	const isValueNotANumber = isNaN(value);
	const isValidUnit = Object.values(UnitSymbolLiteral).includes(unit as UnitSymbolLiteral);

	if (isValueNotANumber) {
		console.warn('convertToML received NaN value');
		return NaN;
	}

	if (!isValidUnit) {
		console.warn('Invalid unit provided to convertToML:', unit);
		return NaN;
	}

	const conversionMap: Record<UnitSymbolLiteral, number> = {
		[UnitSymbolLiteral.L]: 0.000001, // 1L = 0.000001 ML (1/1000000)
		[UnitSymbolLiteral.KL]: 0.001, // 1kL = 0.001 ML (1/1000)
		[UnitSymbolLiteral.ML]: 1, // 1ML = 1 ML
		[UnitSymbolLiteral.M3]: 0.001, // 1m³ = 0.001 ML (1/1000) - same as kL
		[UnitSymbolLiteral.X10M3]: 0.01, // 10m³ = 0.01 ML (1/100)
		[UnitSymbolLiteral.X100M3]: 0.1, // 100m³ = 0.1 ML (1/10)
	};

	const conversionFactor = conversionMap[unit as UnitSymbolLiteral];

	if (conversionFactor === undefined) {
		console.warn('Conversion factor not found for unit:', unit);
		return NaN;
	}

	return value * conversionFactor;
};

export const formatDeviceLabel = (
	device: DeviceSummary | ConnectedDeviceSummary,
	parentDevice: DeviceSummary | undefined,
) => {
	if (parentDevice) {
		const parentString: string = parentDevice.displayName
			? `${parentDevice.reference} (${parentDevice.displayName})`
			: `${parentDevice.reference}`;
		const deviceString: string = device.displayName
			? `${device.reference} (${device.displayName})`
			: device.reference;
		return `${parentString}: ${deviceString}`;
	} else {
		const deviceString: string = device.displayName
			? `${device.reference} (${device.displayName})`
			: device.reference;
		return deviceString;
	}
};

export const formatDeviceName = (
	deviceName: string | undefined,
	deviceReference: string | undefined,
) => {
	return `${deviceReference} ${deviceName ? `(${deviceName})` : ''}`;
};

export const filterDevices = (devices: DeviceSummary[], filters?: LocalDeviceFilter) => {
	if (!filters) return devices;
	return devices.filter(device => {
		if (!filters.type && !filters.deviceStatus && !filters.waterManagementArea) return true;
		if (filters.type && !filters.type.includes(device.type as DeviceType.type)) return false;
		if (filters.deviceStatus && !filters.deviceStatus.includes(device.deviceStatus)) return false;
		if (device.waterManagementArea) {
			if (
				filters.waterManagementArea &&
				!filters.waterManagementArea.includes(device.waterManagementArea)
			) {
				return false;
			}
		} else if (filters.waterManagementArea) {
			return false;
		}
		return true;
	});
};

/**
 * Filters out Modular meter devices that have no connected active Sensor devices
 * @param {DeviceSummary[]} devices - List of devices to filter
 * @returns {DeviceSummary[]} Filtered devices excluding Modular meter devices without active sensors
 */
export const excludeModularDevicesWithoutActiveSensors = (
	devices: DeviceSummary[],
): DeviceSummary[] =>
	devices.filter(device => {
		const isModularMeter = device.type === DeviceType.type.MODULAR_METER;
		return (
			!isModularMeter ||
			(device.connectedDevices ?? []).some(
				connectedDevice =>
					connectedDevice.type === ConnectedDeviceSummary.type.SENSOR &&
					connectedDevice.deviceStatus === DeviceType.deviceStatus.IN_SERVICE,
			)
		);
	});

/**
 * Maps devices to options for the select component
 * @param {Array<ConnectedDeviceSummary | DeviceSummary>} devices - The devices to map
 * @returns {Array<{value: string, label: string}>} The mapped devices
 */
export const mapDevicesToOptions = (
	devices: (ConnectedDeviceSummary | DeviceSummary)[],
): { value: string; label: string }[] => {
	return devices
		.map(device => ({
			value: device.id,
			label: formatDeviceLabel(device, undefined),
		}))
		.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
};
